import { useCallback, useEffect, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { L3_PATHS } from 'Deer/constants/routes';
import { setL2Visible, useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import { useParsedQuery } from 'components/hooks/useParsedQuery';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCurrentProvider,
  useCurrentProvider,
  REVIEW_RESULT_VIEW,
  setSelectedReviewResultView,
  useSelectedReviewResultView,
} from 'reducers/reviewResultSlice';
import { useProfilePage } from 'screens/Profile/hooks/dataHooks';
import { Loader } from 'screens/admin/HelpCenter/helperComponents';
import { REVIEW_TABLE_TYPES } from 'screens/performanceReview/LandingPage/ReviewAudit';
import DividerForPanel from 'screens/performanceReview/components/Sidebar/common/DividerForPanel';
import { useReviewPreviewParams } from 'screens/performanceReview/hooks/reviewPreviewHooks';
import useReviewResultParams from 'screens/performanceReview/hooks/useReviewResultParams';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import userflow from 'userflow.js';
import { checkEnableCompanySupport, checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { Help, MyTeam, QuickHelp } from 'assets/assetsV1/iconComponents';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';
import { NETCHEX_SIDEPANEL_WIDTH } from 'constants/netChexConstants';
import { routes } from 'constants/routes';

import { Avatar } from 'uiToolkitV1/Avatar';
import { ListItemLeftNav } from 'uiToolkitV1/ListItemLeftNav';
import { ResultEmployeeToggle } from 'uiToolkitV1/ResultEmployeeToggle';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import useReviewResultSidebarData from './hooks/dataHooks';

export const SideNavContainer = ({ children, ...props }) => (
  <Box h="100%" display="flex" flexDir="column" p={checkNewNavEnabled() ? 0 : '4px 12px 12px'} {...props}>
    {children}
  </Box>
);

const EmployeeInfo = ({ avatarSize = 24 }) => {
  // API should send this data in sidebar data ideally!!!

  const { employeeId } = useReviewResultParams();
  const { data, isLoading } = useProfilePage({ employeeId });
  const { displayName, designation, profilePhotoSrc } = data ?? {};
  if (isLoading) {
    return null;
  }
  return (
    <Flex mb={1}>
      <Avatar src={profilePhotoSrc} name={displayName} size={avatarSize} />
      <Box ml={2}>
        <TextBodyMedium bold>{displayName}</TextBodyMedium>
        <TextLabel color="text.g2">{designation}</TextLabel>
      </Box>
    </Flex>
  );
};

const ReviewResultSidebar = () => {
  const dispatch = useDispatch();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const { isPreview } = useReviewPreviewParams();

  const { data, isLoading } = useReviewResultSidebarData();

  const [isAutoNavigateToManagerDone, setIsAutoNavigateToManagerDone] = useState(false);
  const [isSelectDefaultProvider, setIsSelectDefaultProvider] = useState(false);

  const isQuestionViewSelected = useSelectedReviewResultView() === REVIEW_RESULT_VIEW.QUESTION;

  let options = Object.keys(data ?? {})?.map((key) => ({
    ...data[key]?.employee,
    providerType: data[key]?.review?.providerType,
    reviewId: data[key]?.review?.id,
    isAccessible: data[key]?.isAccessible,
    peerType: data[key]?.review?.peerType,
  }));
  const { defaultSelectedProviderId, autoNavigate } = useParsedQuery();

  options = options.filter(({ isAccessible }) => !!isAccessible);

  const isMobileViewBool = isMobileView();
  const isL2Visible = useSelectIsL2Visible();

  const closeSidepanelOnClickMobile = useCallback(() => {
    if (isMobileViewBool && isL2Visible) {
      dispatch(
        setL2Visible({
          isL2Visible: false,
        })
      );
    }
  }, [dispatch, isL2Visible, isMobileViewBool]);

  const setProvider = useCallback(
    (value) => {
      dispatch(setCurrentProvider(value));
      closeSidepanelOnClickMobile();
    },
    [dispatch, closeSidepanelOnClickMobile]
  );

  const setViewByQuestions = () => {
    dispatch(setSelectedReviewResultView(REVIEW_RESULT_VIEW.QUESTION));
    closeSidepanelOnClickMobile();
  };

  const currentProvider = useCurrentProvider() ?? {};
  const { reviewId } = currentProvider;

  // run once on first render to close side panel on mobile
  useEffect(() => {
    closeSidepanelOnClickMobile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (autoNavigate) {
      if (isAutoNavigateToManagerDone || checkEnableCompanySupport()) return;
      // select manager from providers
      const managerProvider = options.find(({ providerType }) => providerType === REVIEW_PROVIDER_TYPES.MANAGER);
      if (managerProvider) {
        setProvider(managerProvider);
        setIsAutoNavigateToManagerDone(true);
      }
    } else if (defaultSelectedProviderId) {
      if (isSelectDefaultProvider) return;
      // select manager from providers
      // eslint-disable-next-line no-shadow, eqeqeq
      const defaultSelectedProvider = options.find(({ id }) => id == defaultSelectedProviderId);
      if (defaultSelectedProvider) {
        setProvider(defaultSelectedProvider);
        setIsSelectDefaultProvider(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoNavigate,
    defaultSelectedProviderId,
    isAutoNavigateToManagerDone,
    isSelectDefaultProvider,
    setProvider,
    isLoading,
  ]);

  if (isLoading) {
    return (
      <Box p={4} width={NETCHEX_SIDEPANEL_WIDTH}>
        <Loader />
      </Box>
    );
  }

  return (
    <SideNavContainer>
      <EmployeeInfo
        avatarSize={32}
        name={loggedInEmployee?.displayName}
        photoSrc={loggedInEmployee?.profilePhotoSrc}
        label={loggedInEmployee?.designation}
      />
      <DividerForPanel />
      <ListItemLeftNav
        LeftIconComponent={Help}
        mb={1}
        onClick={setViewByQuestions}
        title="View by questions"
        active={isQuestionViewSelected}
      />
      <DividerForPanel />

      <Box flexGrow={1}>
        {options?.length > 0 && (
          <>
            <TextLabel bold py={1} mb={2} color="text.g2">
              VIEW BY PEOPLE
            </TextLabel>
            <ResultEmployeeToggle selectedOptionId={reviewId} options={options} changeHandler={setProvider} />
          </>
        )}
      </Box>

      {!isPreview && (
        <>
          <DividerForPanel />
          <ListItemLeftNav
            LeftIconComponent={MyTeam}
            mb={1}
            onClick={() => {
              const url = `${
                checkNewNavEnabled() ? L3_PATHS['my-data'].review.myReviews : routes.performanceReview.audit
              }?activeTableId=${REVIEW_TABLE_TYPES.MY_PAST_REVIEWS}`;
              window.open(url, '_blank'); // This will open the URL in a new tab
            }}
            title="View Past Reviews"
          />
        </>
      )}
      {!isMobileView() && !isPreview && !checkNewNavEnabled() && (
        <>
          <DividerForPanel />
          <ListItemLeftNav
            LeftIconComponent={QuickHelp}
            mb={1}
            onClick={() => {
              userflow.toggleResourceCenter();
            }}
            title="Quick Help"
          />
        </>
      )}
    </SideNavContainer>
  );
};

export default ReviewResultSidebar;
